/**
 * Catálogo de reglas de alerta (anexo A3 del brief). Los umbrales de aquí son los valores
 * por defecto; los vigentes viven en la BBDD del panel (`rule_params`) y llegan en cada foto.
 */
export type BlockId = 'J' | 'S' | 'C' | 'F';
export type RuleId =
  | 'J1'
  | 'J2'
  | 'J3'
  | 'S1'
  | 'S2'
  | 'S3'
  | 'S4'
  | 'S5'
  | 'S6'
  | 'S7'
  | 'C1'
  | 'C2'
  | 'C3'
  | 'F1'
  | 'F2'
  | 'F3'
  /** Caso registrado a mano (lo trae el tándem y el sistema no lo detecta). Su bloque y semáforo los decide quien lo registra. */
  | 'M1';
/** d=días, dl=días laborables, %=porcentaje, m=meses, re=reaperturas, R=nivel de riesgo */
export type Unit = 'd' | 'dl' | '%' | 'm' | 're' | 'R';
/** Disponibilidad del dato: ok=hoy, warn=corto plazo, bad=no disponible */
export type SourceStatus = 'ok' | 'warn' | 'bad';

export interface Thresholds {
  amber: number;
  red: number;
  /** true: cuanto más bajo, peor */
  inv: boolean;
}

export interface RuleDef extends Thresholds {
  id: RuleId;
  block: BlockId;
  name: string;
  measure: string;
  unit: Unit;
  source: SourceStatus;
  sourceLabel: string;
  /** Se calcula en esta iteración. Si es false, la regla se omite sin romper la pantalla. */
  enabled: boolean;
  /**
   * Segundo juego de umbrales cuando el caso no tiene la referencia principal (S1: sin vencimiento ni SLO,
   * se mide la antigüedad). Cada alerta se guarda con los umbrales que le tocaron.
   */
  fallback?: { measure: string; amber: number; red: number };
}

export interface BlockDef {
  id: BlockId;
  name: string;
  minutes: number;
}

export const BLOCKS: readonly BlockDef[] = [
  { id: 'J', name: 'Juntas y compromisos', minutes: 20 },
  { id: 'S', name: 'Servicio y operación', minutes: 20 },
  { id: 'C', name: 'Cliente y riesgo', minutes: 15 },
  { id: 'F', name: 'Finanzas operativas', minutes: 15 },
];

const r = (
  id: RuleId,
  block: BlockId,
  name: string,
  measure: string,
  unit: Unit,
  amber: number,
  red: number,
  source: SourceStatus,
  sourceLabel: string,
  opts: { inv?: boolean; enabled?: boolean; fallback?: RuleDef['fallback'] } = {},
): RuleDef => ({
  id,
  block,
  name,
  measure,
  unit,
  amber,
  red,
  source,
  sourceLabel,
  inv: opts.inv ?? false,
  enabled: opts.enabled ?? false,
  ...(opts.fallback && { fallback: opts.fallback }),
});

/** Iteración 1: solo J1, S1, S2 y S3 están habilitadas (reglas «hoy» del brief, F1 omitida). */
export const DEFAULT_RULES: Readonly<Record<RuleId, RuleDef>> = {
  J1: r('J1', 'J', 'Acta sin notificar', 'Días desde la junta', 'd', 8, 10, 'ok', 'Eleva · juntas', {
    enabled: true,
  }),
  J2: r(
    'J2',
    'J',
    'Junta con contabilidad sin cerrar',
    'Días hasta la junta',
    'd',
    28,
    14,
    'bad',
    'Contabilidad · cierres',
    { inv: true },
  ),
  J3: r(
    'J3',
    'J',
    'Compromiso de junta abierto',
    'Antigüedad del compromiso',
    'd',
    60,
    90,
    'warn',
    'Eleva · tickets «Acuerdo Junta»',
  ),
  // Retraso sobre la fecha de vencimiento del ticket; si no tiene, sobre el SLO de su subcategoría/categoría;
  // si tampoco, su antigüedad (con los umbrales de `fallback`)
  S1: r(
    'S1',
    'S',
    'Incidencia fuera de plazo',
    'Días de retraso (vencimiento o SLO)',
    'd',
    1,
    7,
    'ok',
    'Eleva · tickets',
    {
      enabled: true,
      fallback: { measure: 'Días abierta (sin vencimiento ni SLO)', amber: 15, red: 30 },
    },
  ),
  S2: r('S2', 'S', 'Incidencia sin movimiento', 'Días sin movimiento', 'd', 7, 15, 'ok', 'Eleva · tickets', {
    enabled: true,
  }),
  S3: r('S3', 'S', 'Incidencia reabierta', 'Reaperturas del caso', 're', 2, 3, 'ok', 'Eleva · tickets', {
    enabled: true,
  }),
  S4: r(
    'S4',
    'S',
    'Siniestro sin avance',
    'Días sin avance',
    'd',
    15,
    30,
    'warn',
    'Eleva · siniestros (claims)',
  ),
  S5: r(
    'S5',
    'S',
    'Presupuesto sin recibir',
    'Días desde la solicitud',
    'd',
    10,
    20,
    'warn',
    'Eleva · presupuestos',
  ),
  S6: r(
    'S6',
    'S',
    'Presupuesto sin presentar',
    'Días desde la recepción',
    'd',
    7,
    15,
    'warn',
    'Eleva · presupuestos',
  ),
  S7: r(
    'S7',
    'S',
    'Obra aceptada sin inicio',
    'Días desde la aceptación',
    'd',
    21,
    45,
    'warn',
    'Obria · obras',
  ),
  C1: r('C1', 'C', 'Reclamación parada', 'Días en el mismo estado', 'd', 5, 10, 'bad', 'Atención al cliente'),
  C2: r(
    'C2',
    'C',
    'Reclamación sin solución',
    'Días laborables sin solución',
    'dl',
    7,
    10,
    'bad',
    'Atención al cliente',
  ),
  C3: r('C3', 'C', 'Riesgo de baja', 'Nivel declarado', 'R', 2, 3, 'bad', 'Campo nuevo · mensual 1 a 1'),
  F1: r(
    'F1',
    'F',
    'Cobertura de caja',
    'Meses de gasto que se pueden pagar con lo disponible',
    'm',
    1.5,
    1,
    'ok',
    'Gesfincas · saldos contables',
    { inv: true, enabled: true },
  ),
  F2: r(
    'F2',
    'F',
    'Índice de morosidad',
    'Impagados sobre el gasto anual',
    '%',
    10,
    15,
    'ok',
    'Gesfincas · saldos contables',
    { enabled: true },
  ),
  F3: r('F3', 'F', 'Alta sin facturar', 'Días desde el alta', 'd', 30, 60, 'warn', 'Facturación'),
  // Sin umbrales: el semáforo lo elige quien registra el caso. No genera hechos en la foto ni cuenta en los KPIs.
  M1: r('M1', 'S', 'Caso manual', 'Días desde que se registró', 'd', 0, 0, 'ok', 'Registro manual', {
    enabled: true,
  }),
};

export const RULE_IDS = Object.keys(DEFAULT_RULES) as RuleId[];
export const MANUAL_RULE: RuleId = 'M1';
/** Reglas calculadas desde el ERP (excluye la de casos manuales) */
export const SYSTEM_RULE_IDS = RULE_IDS.filter((id) => id !== MANUAL_RULE);

export const SOURCE_LABEL: Record<SourceStatus, string> = {
  ok: 'Hoy',
  warn: 'Corto plazo',
  bad: 'No disponible',
};
